const TIER_NAME_BY_LEVEL = {
  STARTER: "Starter",
  GROWTH: "Growth",
  AUTHORITY: "Authority",
};

const getPlanLevelKey = (planName = "") => planName.trim().toUpperCase();

const formatCurrency = (amount) => {
  const numericAmount = Number(amount);

  if (Number.isNaN(numericAmount)) {
    return null;
  }

  const formattedAmount = Number.isInteger(numericAmount)
    ? numericAmount.toLocaleString("en-US")
    : numericAmount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

  return `$${formattedAmount}`;
};

const getPriceForDuration = (subscriptionFees = [], durationType) => {
  for (const fee of subscriptionFees) {
    // Support priceList array (legacy BAMOE format)
    for (const price of fee?.priceList || []) {
      if (price?.priceDurationType === durationType && price?.priceAmt != null) {
        return price.priceAmt;
      }
    }
    // Support single price object (new subscription-plan API format)
    if (fee?.price?.priceDurationType === durationType && fee?.price?.priceAmt != null) {
      return fee.price.priceAmt;
    }
  }

  return null;
};

const normalizeApiPlans = (apiPlans) => {
  if (Array.isArray(apiPlans)) {
    return apiPlans;
  }

  if (Array.isArray(apiPlans?.data)) {
    return apiPlans.data;
  }

  if (Array.isArray(apiPlans?.payload)) {
    return apiPlans.payload;
  }

  if (Array.isArray(apiPlans?.payload?.data)) {
    return apiPlans.payload.data;
  }

  return [];
};

/**
 * Consolidates plans that are split by paymentFrequencyType (MONTH/YEAR)
 * into a single plan per tierLevel with combined subscriptionFees.
 */
const consolidatePlansByTier = (plans, businessDomain) => {
  const tierMap = new Map();

  for (const plan of plans) {
    if (plan?.businessDomain !== businessDomain || !plan?.tierLevel) {
      continue;
    }

    const tierKey = String(plan.tierLevel).toUpperCase();

    if (!tierMap.has(tierKey)) {
      tierMap.set(tierKey, {
        ...plan,
        subscriptionFees: [...(plan.subscriptionFees || [])],
      });
    } else {
      // Merge subscription fees from plans with same tier but different frequency
      const existing = tierMap.get(tierKey);
      existing.subscriptionFees = [
        ...existing.subscriptionFees,
        ...(plan.subscriptionFees || []),
      ];
    }
  }

  return tierMap;
};

export const mergePricingPlansWithApi = ({
  apiPlans = [],
  fallbackPlans = [],
  businessDomain,
  tierOrder = [],
}) => {
  const normalizedApiPlans = normalizeApiPlans(apiPlans);

  if (!normalizedApiPlans.length) {
    return [];
  }

  const apiPlansByTier = consolidatePlansByTier(normalizedApiPlans, businessDomain);

  return fallbackPlans.map((fallbackPlan, index) => {
    const fallbackTierKey = tierOrder[index];
    const apiPlan =
      apiPlansByTier.get(getPlanLevelKey(fallbackPlan.name)) ||
      (fallbackTierKey ? apiPlansByTier.get(String(fallbackTierKey).toUpperCase()) : null);

    if (!apiPlan) {
      return fallbackPlan;
    }

    const monthlyPrice = getPriceForDuration(apiPlan.subscriptionFees, "MONTH");
    const yearlyPrice = getPriceForDuration(apiPlan.subscriptionFees, "YEAR");
    const oneTimeSetupFee = getPriceForDuration(apiPlan.subscriptionFees, "ONE_TIME");

    return {
      ...fallbackPlan,
      name:
        TIER_NAME_BY_LEVEL[String(apiPlan.tierLevel).toUpperCase()] ||
        fallbackPlan.name,
      price: monthlyPrice != null ? formatCurrency(monthlyPrice) : null,
      yearlyPrice: yearlyPrice != null ? formatCurrency(yearlyPrice) : null,
      setupFeeText:
        oneTimeSetupFee != null
          ? `${formatCurrency(oneTimeSetupFee)} one-time setup fee`
          : null,
    };
  }).filter((plan) => plan.price || plan.yearlyPrice);
};
